import { createFileRoute, Link } from "@tanstack/react-router";
import { withProfileDateTime } from "@/lib/schema-dates";
import { Star } from "lucide-react";
import { ORG_REF, PHYSICIAN_ID } from "@/lib/medical-schema";
import drFitzer from "@/assets/dr-fitzer-800.webp.asset.json";
import drFitzer480 from "@/assets/dr-fitzer-480.webp.asset.json";
import { Flower } from "@/components/site/Flower";
import { PageSection } from "@/components/site/PageSection";
import { EsPageShell } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";

export const Route = createFileRoute("/es/sobre-el-doctor")({
  head: () => {
    const base = esHead("about");
    return {
      ...base,
      // Match /about: profile OG type + profile fields (esHead defaults to website).
      meta: [
      ...base.meta.map((m) =>
          "property" in m && m.property === "og:type" ? { ...m, content: "profile" } : m,
        ),
      ...(import.meta.env.SSR ? [
        { property: "profile:first_name", content: "Matthew" },
        { property: "profile:last_name", content: "Fitzer" },
      ] : []),
    ],
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                ...withProfileDateTime(esWebPageSchema("about")),
                "@type": ["AboutPage", "ProfilePage"],
                about: { "@id": PHYSICIAN_ID },
                mainEntity: { "@id": PHYSICIAN_ID },
                primaryImageOfPage: { "@id": `${SITE}/es/sobre-el-doctor#portrait` },
              },
              {
                "@type": "ImageObject",
                "@id": `${SITE}/es/sobre-el-doctor#portrait`,
                contentUrl: `${SITE}${drFitzer.url}`,
                url: `${SITE}${drFitzer.url}`,
                width: 800,
                height: 1066,
                caption:
                  "Retrato del Dr. Matthew Fitzer, cirujano bariátrico certificado en Virginia Bariatric Surgery",
                representativeOfPage: true,
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsAboutPage,
});

function EsAboutPage() {
  return (
    <EsPageShell
      page="about"
      title="Matthew Fitzer, M.D."
      intro="Cirujano bariátrico y médico"
    >
      <PageSection className="relative pt-4 pb-5 grid gap-8 md:grid-cols-2 items-center">
        <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
        <div className="relative">
          <div className="absolute -inset-4 rounded-2xl bg-brand-soft -z-10" />
          <figure className="relative grid grid-cols-5 grid-rows-2 gap-3 aspect-[5/4]">
            <img
              src={drFitzer.url}
              srcSet={`${drFitzer480.url} 480w, ${drFitzer.url} 800w`}
              sizes="(min-width: 768px) 30vw, 60vw"
              alt="Retrato del Dr. Matthew Fitzer, cirujano bariátrico certificado en Virginia Bariatric Surgery"
              width={800}
              height={1066}
              decoding="async"
              className="col-span-3 row-span-2 h-full w-full rounded-xl object-cover shadow-xl"
            />
            <img
              src="/img/dr-fitzer-surgery-400.webp"
              srcSet="/img/dr-fitzer-surgery-400.webp 400w, /img/dr-fitzer-surgery-800.webp 800w"
              sizes="(min-width: 768px) 20vw, 40vw"
              alt="El Dr. Fitzer realizando cirugía bariátrica laparoscópica en el quirófano"
              width={1236}
              height={1075}
              loading="lazy"
              decoding="async"
              className="col-span-2 row-span-1 h-full w-full rounded-xl object-cover shadow-2xl ring-4 ring-background"
            />
            <img
              src="/img/dr-fitzer-patient-400.webp"
              srcSet="/img/dr-fitzer-patient-400.webp 400w, /img/dr-fitzer-patient-800.webp 800w"
              sizes="(min-width: 768px) 20vw, 40vw"
              alt="El Dr. Fitzer con un paciente agradecido después de la cirugía bariátrica"
              width={1575}
              height={1316}
              loading="lazy"
              decoding="async"
              className="col-span-2 row-span-1 h-full w-full rounded-xl object-cover shadow-2xl ring-4 ring-background"
            />
          </figure>
        </div>
        <div>
          {/* Name + subtitle render once in the shell H1/intro above; English
              /about uses the name as its single H1, so we don't repeat it here. */}
          <a
            href="https://www.google.com/search?q=Dr.+Matthew+A.+Fitzer,+MD"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground/80 hover:bg-brand-soft/40"
            aria-label="El Dr. Matthew Fitzer tiene una calificación de 4.7 sobre 5 estrellas de 37 reseñas de Google. Abre el perfil de Google Business en una pestaña nueva."
          >
            <span className="flex items-center gap-0.5 text-brand-logo" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={15} className={i < 4 ? "fill-current" : "fill-current opacity-60"} />
              ))}
            </span>
            <span>
              <strong className="font-semibold text-foreground">4.7</strong> de 37 reseñas de Google
            </span>
          </a>

          <div className="mt-6 space-y-4 text-foreground/80 leading-[1.6]">
            <p>
              El Dr. Matthew Fitzer es un cirujano bariátrico con formación en fellowship que desde 2005 se ha enfocado en brindar una atención excelente al paciente con obesidad. Desde 2012 atiende pacientes en Herndon, Reston, Fairfax, Arlington, Alexandria, el condado de Loudoun y el área metropolitana de Washington D.C.
            </p>
            <p>
              Está certificado por la American Board of Surgery y es fellow del American College of Surgeons y de la American Society of Metabolic and Bariatric Surgeons. Ha fundado y dirigido como director médico programas de cirugía bariátrica certificados por MBSAQIP en varias instituciones. Ha escrito capítulos de libros y artículos publicados en revistas prestigiosas, incluyendo el <em>New England Journal of Medicine</em>.
            </p>
            <p>
              El Dr. Fitzer ha realizado{" "}
              <Link to="/es/cirugia-bariatrica-virginia" className="font-medium text-brand-logo hover:underline">
                más de 6,000 operaciones laparoscópicas de pérdida de peso
              </Link>{" "}
              y ha recetado{" "}
              <Link to="/es/medicamentos-para-bajar-de-peso" className="font-medium text-brand-logo hover:underline">
                medicamentos para bajar de peso
              </Link>{" "}
              a más de 10,000 pacientes en más de 21 años ayudando a las personas a alcanzar sus metas de pérdida de peso.
            </p>
          </div>
        </div>
      </PageSection>

      <PageSection fullWidth maxWidth="4xl" className="bg-brand-soft/30 pt-4 pb-4" innerClassName="space-y-6 text-foreground/80 leading-[1.6]">
        <h2 className="text-2xl md:text-3xl font-serif text-foreground">
          Formación y trayectoria
        </h2>
        <p>
          El Dr. Fitzer creció en Newport News, Virginia. Obtuvo una licenciatura en física en Christopher Newport University y su doctorado en medicina en Eastern Virginia Medical School. Completó su residencia en cirugía general en Memorial Health University Medical Center en Savannah, Georgia, donde desarrolló su interés en la cirugía de pérdida de peso.
        </p>
        <p>
          En 2005 se mudó de Savannah para completar un fellowship de un año en cirugía bariátrica mínimamente invasiva en la Universidad de Missouri, donde estudió con cirujanos de reconocimiento nacional como Steven Scott, Roger de la Torre, Bruce Ramshaw y Steve Eubanks, un gurú de la{" "}
          <Link to="/es/reparacion-de-hernia-hiatal" className="font-medium text-brand-logo hover:underline">
            reparación laparoscópica de hernia hiatal
          </Link>
          .
        </p>
        <p>
          Además de su activa práctica quirúrgica, el Dr. Fitzer tiene más de veinte años de experiencia atendiendo pacientes que prefieren un programa de pérdida de peso sin cirugía. Ha recetado medicamentos inyectables GLP-1 para bajar de peso desde 2006. Su práctica quirúrgica en Herndon, Virginia, se basa en los mismos principios que aprendió durante su fellowship: técnica laparoscópica de alto volumen, selección conservadora de pacientes y seguimiento a largo plazo.
        </p>
      </PageSection>

      <PageSection fullWidth maxWidth="4xl" className="pt-4 pb-4" innerClassName="text-foreground/80 leading-[1.6]">
        <h2 className="text-2xl md:text-3xl font-serif text-foreground text-center">
          Contacto
        </h2>
        <div className="mt-6 grid gap-6 sm:grid-cols-3 text-[0.95rem]">
          <div className="text-center">
            <h3 className="font-semibold text-foreground">Teléfono</h3>
            <p className="mt-2">
              <a href="tel:+17037099771" className="text-brand-deep hover:underline">
                (703) 709-9771
              </a>
            </p>
          </div>
          <div className="text-center">
            <h3 className="font-semibold text-foreground">Dirección del consultorio</h3>
            <address className="not-italic mt-2">
              171 Elden Street, Suite 3B<br />
              Herndon, VA 20170
            </address>
          </div>
          <div className="text-center">
            <h3 className="font-semibold text-foreground">Horario</h3>
            <p className="mt-2">
              Lunes a viernes<br />
              8:00 a.m. – 4:30 p.m.
            </p>
          </div>
        </div>
      </PageSection>

      <PageSection className="pt-4 pb-4 text-center">
        <h2 className="text-2xl md:text-3xl font-serif">
          ¿Listo para hablar?
        </h2>
        <p className="mt-4 text-muted-foreground">
          Es un día perfecto para dar el primer paso.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/es/contacto" className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
            Contáctenos
          </Link>
          <Link to="/es/perdida-de-peso-con-medicamentos" className="rounded-full border border-brand-deep px-7 py-3 text-sm font-medium text-brand-deep hover:bg-brand-soft/60">
            Pérdida de peso con medicamentos
          </Link>
          <Link to="/es/manga-gastrica" className="rounded-full border border-brand-deep px-7 py-3 text-sm font-medium text-brand-deep hover:bg-brand-soft/60">
            Cirugía bariátrica
          </Link>
        </div>
      </PageSection>
    </EsPageShell>
  );
}
